
import React, { useState, useEffect, useRef, Suspense } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import {
  ProductBatchSummaryItem,
  ProductBatchMetadata,
  ProductBatchEditDetails,
  ProductBatchFilterParams,
} from "../../models/productbatch-model";
import productBatchService from "../../services/productbatch/productBatchService";
import ProductBatchDataTable from "./ProductBatchDataTable";
import ProductBatchDialog from "./ProductBatchDialog";
import AppLoading from "../app-loading/app-loading";
import posService from "../../services/pos/pos.service";

const ProductBatchSummary: React.FC = () => {
  const toast = useRef<Toast>(null);

  // Data states
  const [batches, setBatches] = useState<ProductBatchSummaryItem[]>([]);
  const [metadata, setMetadata] = useState<ProductBatchMetadata>({
    sourceTypes: [],
    vendors: [],
    subCategories: [],
    products: [],
  });
  const [editingBatch, setEditingBatch] = useState<ProductBatchSummaryItem | ProductBatchEditDetails | null>(null);

  // Filter states
  const [sourceTypeId, setSourceTypeId] = useState<number | null>(null);
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [subCategoryId, setSubCategoryId] = useState<number | null>(null);
  const [productId, setProductId] = useState<number | null>(null);
  const [manufacturingUnitId, setManufacturingUnitId] = useState<number | null>(null);
  const [manufacturingUnitOptions, setManufacturingUnitOptions] = useState<{ label: string; value: number }[]>([]);
  const [manufactureDateFrom, setManufactureDateFrom] = useState<Date | null>(null);
  const [manufactureDateTo, setManufactureDateTo] = useState<Date | null>(null);
  const [expiryDateFrom, setExpiryDateFrom] = useState<Date | null>(null);
  const [expiryDateTo, setExpiryDateTo] = useState<Date | null>(null);
  const [batchCode, setBatchCode] = useState<string>("");
  const [onlyAvailableStockLessThanOne, setOnlyAvailableStockLessThanOne] = useState(false);
  const [onlyHidden, setOnlyHidden] = useState(false);


  // Pagination states
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sort states
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [resetTriggered, setResetTriggered] = useState(false);


  // UI states
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  // Initialize
  useEffect(() => {
    loadMetadata();
    loadBatches();
  }, []);

  useEffect(() => {
    const loadManufacturingUnits = async () => {
      try {
        const response = await posService.getAllPOS();
        const options = (response || []).map((mu: any) => {
          const posCode = mu.posCode?.trim();
          const posId = Number(mu.posID ?? mu.posId);
          const directAlias =
            mu.posAlias?.trim() ||
            mu.POSAlias?.trim() ||
            mu.PosAlias?.trim() ||
            mu.pos_alias?.trim();
          const posAlias =
            directAlias ||
            (posId === 1 || posCode === "POS-001"
              ? "NGBV"
              : posId === 2 || posCode === "POS-002"
                ? "MYS"
                : posId === 3 || posCode === "POS-003" || posCode === "KRN1"
                  ? "KRN1"
                  : posId === 4 || posCode === "POS-004" || posCode === "MGT"
                    ? "MGT"
                    : posId === 5 || posCode === "POS-005" || posCode === "KRN2"
                      ? "KRN2"
                      : posId === 6 || posCode === "POS-006" || posCode === "NGBV2"
                        ? "NGBV2"
                        : "");

          return {
            label: posCode && posAlias
              ? `${posCode} - ${posAlias}`
              : posCode || posAlias || `Unit-${mu.posID}`,
            value: Number(mu.posID ?? mu.posId),
          };
        });

        setManufacturingUnitOptions(options);
      } catch (error) {
        console.error("Error loading manufacturing units:", error);
      }
    };

    loadManufacturingUnits();
  }, []);

  // Load metadata
  const loadMetadata = async () => {
    try {
      const data = await productBatchService.getMetadata();
      setMetadata(data);
    } catch (error) {
      console.error("Error loading metadata:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load metadata",
        life: 3000,
      });
    }
  };

  // Load batches with filters
  const loadBatches = async () => {
    try {
      setLoading(true);

      const filters: ProductBatchFilterParams = {
        sourceTypeId: sourceTypeId || null,
        vendorId: vendorId || null,
        subCategoryId: subCategoryId || null,
        productId: productId || null,
        ManufacturingUnitPosId: manufacturingUnitId || null,
        batchCode: batchCode || null,
        manufactureDateFrom: manufactureDateFrom ? manufactureDateFrom.toISOString().split("T")[0] : null,
        manufactureDateTo: manufactureDateTo ? manufactureDateTo.toISOString().split("T")[0] : null,
        expiryDateFrom: expiryDateFrom ? expiryDateFrom.toISOString().split("T")[0] : null,
        expiryDateTo: expiryDateTo ? expiryDateTo.toISOString().split("T")[0] : null,
        sortBy: sortBy || null,
        sortOrder: sortOrder || null,
        pageNumber,
        pageSize,
        onlyAvailableStockLessThanOne: onlyAvailableStockLessThanOne || null,
        onlyHidden: onlyHidden || null,
      };

      const response = await productBatchService.getSummary(filters);
      setBatches(response.batches);
      setTotalRecords(response.totalRecords);
    } catch (error) {
      console.error("Error loading batches:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load batches",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Reload batches when filters or pagination changes
  useEffect(() => {
    loadBatches();
  }, [pageNumber, pageSize, sortBy, sortOrder, onlyAvailableStockLessThanOne, onlyHidden]);

  const handleFilterChange = () => {
    setPageNumber(1); // Reset to first page when filtering
    loadBatches();
  };

  const handleResetFilters = () => {
    setSourceTypeId(null);
    setVendorId(null);
    setSubCategoryId(null);
    setProductId(null);
    setManufacturingUnitId(null);
    setManufactureDateFrom(null);
    setManufactureDateTo(null);
    setExpiryDateFrom(null);
    setExpiryDateTo(null);
    setPageNumber(1);
    setSortBy(null);
    setSortOrder(null);
    setLoading(true);
    setResetTriggered(true);
    setBatchCode("");
    setOnlyAvailableStockLessThanOne(false);
    setOnlyHidden(false);
  };

  useEffect(() => {
    if (resetTriggered) {
      loadBatches();
      setResetTriggered(false);
    }
  }, [resetTriggered]);

  // useEffect(() => {
  //   if (loading && sourceTypeId === null && vendorId === null && subCategoryId === null && productId === null) {
  //     loadBatches();
  //   }
  // }, [sourceTypeId, vendorId, subCategoryId, productId]);



  const handleExport = async () => {
    try {
      setLoading(true);

      const filters = {
        sourceTypeId: sourceTypeId || null,
        vendorId: vendorId || null,
        subCategoryId: subCategoryId || null,
        productId: productId || null,
        manufacturingUnitId: manufacturingUnitId || null,
        manufactureDateFrom: manufactureDateFrom ? manufactureDateFrom.toISOString().split("T")[0] : null,
        manufactureDateTo: manufactureDateTo ? manufactureDateTo.toISOString().split("T")[0] : null,
        expiryDateFrom: expiryDateFrom ? expiryDateFrom.toISOString().split("T")[0] : null,
        expiryDateTo: expiryDateTo ? expiryDateTo.toISOString().split("T")[0] : null,
        sortBy: sortBy || null,
        sortOrder: sortOrder || null,
        batchCode: batchCode || null,
      };

      const blob = await productBatchService.export(filters);

      // Create blob URL and download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `product-batch-summary-${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Data exported successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to export data",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddBatch = () => {
    setEditingBatch(null);
    setShowDialog(true);
  };

  const handleEditBatch = async (batch: ProductBatchSummaryItem) => {
    try {
      setLoading(true);
      const details = await productBatchService.getById(batch.batchId);
      if (details) {
        setEditingBatch(details);
        setShowDialog(true);
      }
    } catch (error) {
      console.error("Error loading batch details:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load batch details",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setEditingBatch(null);
  };

  const handleDialogSuccess = () => {
    handleDialogClose();
    setPageNumber(1);
    loadBatches();
  };

  const sourceTypeOptions = metadata.sourceTypes.map((st) => ({
    label: st.name,
    value: st.id,
  }));

  const vendorOptions = metadata.vendors.map((v) => ({
    label: v.name,
    value: v.id,
  }));

  const subCategoryOptions = metadata.subCategories.map((sc) => ({
    label: sc.name,
    value: sc.id,
  }));

  const productOptions = metadata.products.map((p) => ({
    label: p.name,
    value: p.id,
  }));

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <Toast ref={toast} />

        <style>{`
          .complaint-header-card {
              background: linear-gradient(135deg, #f8fffe 0%, #e8f5e9 100%);
              border-radius: 16px;
              padding: 1.5rem 2rem;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
              border: 1px solid rgba(76, 175, 80, 0.1);
              margin-bottom: 1.5rem;
          }

          .complaint-title-wrapper {
              display: flex;
              align-items: center;
              gap: 0.75rem;
          }

          .complaint-icon-wrapper {
              width: 48px;
              height: 48px;
              background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
          }

          .complaint-icon-wrapper i {
              color: white;
              font-size: 1.5rem;
          }

          .register-complaint-btn {
              background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important;
              border: none !important;
              border-radius: 10px !important;
              padding: 0.65rem 1.5rem !important;
              font-weight: 600 !important;
              box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
              transition: all 0.3s ease !important;
              color: #ffffff !important;
          }

          .register-complaint-btn:hover {
              transform: translateY(-2px) !important;
              box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
          }

          .register-complaint-btn:active {
              transform: translateY(0) !important;
          }

          /* Search panel header */
          .complaint-search-container .search-panel-header {
              display: flex;
              align-items: center;
              gap: 0.5rem;
              margin-bottom: 1rem;
              padding-bottom: 0.75rem;
              border-bottom: 1px solid #f0f0f0;
          }

          .complaint-search-container .search-panel-header i {
              color: #4CAF50;
              font-size: 1rem;
          }

          .complaint-search-container .search-panel-header span {
              font-weight: 600;
              color: #374151;
              font-size: 0.9rem;
          }

          .complaint-search-container .p-dropdown,
          .complaint-search-container .p-calendar,
          .complaint-search-container .batch-code-input {
              height: 34px !important;
              min-height: 34px !important;
              font-size: 0.8125rem !important;
              border-radius: 8px !important;
              border-color: #e0e0e0 !important;
              width: 100% !important;
          }

          .complaint-search-container .p-dropdown .p-dropdown-label {
              padding: 0.4rem 0.75rem !important;
              font-size: 0.8125rem !important;
          }

          .complaint-search-container .p-dropdown-trigger {
              width: 2rem !important;
          }

          .complaint-search-container .p-calendar .p-inputtext,
          .complaint-search-container .batch-code-input {
              padding: 0.4rem 0.75rem !important;
              font-size: 0.8125rem !important;
          }

          .complaint-search-container .p-calendar .p-button {
              width: 2rem !important;
              padding: 0 !important;
          }

          .complaint-search-container .p-button {
              height: 34px !important;
              min-height: 34px !important;
              padding: 0 0.75rem !important;
              font-size: 0.8125rem !important;
          }

          .complaint-search-container .p-button-icon-only {
              width: 34px !important;
              padding: 0 !important;
          }

          .complaint-search-container .p-dropdown:hover,
          .complaint-search-container .p-calendar:hover,
          .complaint-search-container .batch-code-input:hover {
              border-color: #4CAF50 !important;
          }

          .complaint-search-container .p-dropdown:focus-within,
          .complaint-search-container .p-calendar:focus-within,
          .complaint-search-container .batch-code-input:focus {
              border-color: #4CAF50 !important;
              box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1) !important;
          }

          .complaint-search-dropdown-panel.p-dropdown-panel {
              border-radius: 12px !important;
              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
              border: 1px solid #f0f0f0 !important;
              padding: 0.5rem !important;
          }

          .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items {
              padding: 0 !important;
          }

          .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
              border-radius: 8px !important;
              margin: 2px 0 !important;
              padding: 0.6rem 1rem !important;
              font-size: 0.875rem !important;
              transition: all 0.2s ease !important;
          }

          .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
              background: #4CAF50 !important;
              color: #ffffff !important;
          }

          .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
              background: #4CAF50 !important; 
              color: #ffffff !important;
              font-weight: 600 !important;
          }

          .complaint-search-container .search-button-primary {
              background: #4CAF50 !important;
              border: none !important;
              border-radius: 8px !important;
              color: #ffffff !important;
          }
          .complaint-search-container .search-button-primary:hover {
              background: #45a049 !important;
          }

          .complaint-search-container .search-button-secondary {
              background: transparent !important;
              border: 1px solid #4CAF50 !important;
              color: #4CAF50 !important;
              border-radius: 8px !important;
          }
          .complaint-search-container .search-button-secondary:hover {
              background: rgba(76, 175, 80, 0.05) !important;
              color: #45a049 !important;
              border-color: #45a049 !important;
          }

          .complaint-search-container .compact-grid {
              display: flex !important;
              flex-wrap: wrap !important;
              gap: 1rem !important;
              align-items: center !important;
          }

          .complaint-search-container .compact-grid > div {
              flex: 0 0 auto !important;
              margin-bottom: 0 !important;
          }

          .complaint-search-container .type-filter { width: 140px !important; }
          .complaint-search-container .date-filter { width: 140px !important; }
          .complaint-search-container .customer-filter { width: 180px !important; }
          .complaint-search-container .button-group { width: auto !important; }

          @media screen and (max-width: 960px) {
              .complaint-search-container .compact-grid {
                  gap: 0.5rem !important;
              }
              
              .complaint-search-container .type-filter,
              .complaint-search-container .date-filter,
              .complaint-search-container .customer-filter { 
                  width: calc(50% - 0.5rem) !important; 
                  flex: 1 1 auto !important;
              }

              .complaint-search-container .button-group {
                  width: 100% !important;
                  justify-content: flex-end;
                  margin-top: 0.5rem;
              }
          }

          @media screen and (max-width: 576px) {
              .complaint-search-container .type-filter,
              .complaint-search-container .date-filter,
              .complaint-search-container .customer-filter { 
                  width: 100% !important; 
              }
          }

          .complaint-search-datepicker.p-datepicker {
              min-width: 250px !important;
              width: 250px !important;
              padding: 0.5rem !important;
              font-size: 0.8rem !important;
              border-radius: 12px !important;
              box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          }

          .complaint-search-datepicker.p-datepicker .p-datepicker-header {
              padding: 0.25rem !important;
              margin-bottom: 0.25rem !important;
              border-top-left-radius: 12px !important;
              border-top-right-radius: 12px !important;
          }

          .complaint-search-datepicker .p-datepicker-title,
          .complaint-search-datepicker .p-datepicker-title .p-datepicker-month,
          .complaint-search-datepicker .p-datepicker-title .p-datepicker-year {
              font-size: 0.9rem !important;
              padding: 0.1rem !important;
              margin: 0 !important;
          }

          .complaint-search-datepicker table th {
              padding: 0.2rem !important;
              font-size: 0.75rem !important;
          }

          .complaint-search-datepicker table td {
              padding: 0 !important;
          }

          .complaint-search-datepicker table td > span {
              width: 1.75rem !important;
              height: 1.75rem !important;
              font-size: 0.8rem !important;
              margin: 1px auto !important;
          }

          .complaint-search-container .filter-label {
              display: none !important;
          }
      `}</style>

        <div className="layout-content mt-3 mb-3">
          <div className="complaint-header-card">
            <div className="flex align-items-center justify-content-between">
              <div>
                <div className="complaint-title-wrapper">
                  <div className="complaint-icon-wrapper">
                    <i className="pi pi-box"></i>
                  </div>
                  <div>
                    <h1 className="text-800 font-bold text-3xl m-0" style={{ color: "#10492a" }}>
                      Product Batch
                    </h1>
                    <p className="text-600 m-0 text-sm mt-1">
                      Track, manage, and monitor product batches efficiently
                    </p>
                  </div>
                </div>
              </div>
              <Button
                label="Add Batch"
                icon="pi pi-plus"
                onClick={handleAddBatch}
                className="register-complaint-btn"
              />
            </div>
          </div>

          <div className="complaint-search-container surface-card p-3 shadow-2 border-round-xl mt-3" style={{
            border: '1px solid rgba(76, 175, 80, 0.15)',
            background: '#ffffff'
          }}>
            <div className="search-panel-header">
              <i className="pi pi-filter-fill"></i>
              <span>Filter Batches</span>
            </div>

            <div className="compact-grid">
              <div className="type-filter">
                <label className="filter-label">Source Type</label>
                <Dropdown
                  value={sourceTypeId}
                  onChange={(e) => {
                    setSourceTypeId(e.value);
                    setPageNumber(1);
                  }}
                  options={sourceTypeOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Source Type"
                  showClear
                  disabled={loading}
                  filter
                  panelClassName="complaint-search-dropdown-panel"
                />
              </div>

              <div className="type-filter">
                <label className="filter-label">Vendor</label>
                <Dropdown
                  value={vendorId}
                  onChange={(e) => {
                    setVendorId(e.value);
                    setPageNumber(1);
                  }}
                  options={vendorOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Vendor"
                  showClear
                  disabled={loading}
                  filter
                  panelClassName="complaint-search-dropdown-panel"
                />
              </div>

              <div className="type-filter">
                <label className="filter-label">Sub Category</label>
                <Dropdown
                  value={subCategoryId}
                  onChange={(e) => {
                    setSubCategoryId(e.value);
                    setPageNumber(1);
                  }}
                  options={subCategoryOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Sub Category"
                  showClear
                  disabled={loading}
                  filter
                  panelClassName="complaint-search-dropdown-panel"
                />
              </div>

              <div className="type-filter">
                <label className="filter-label">Product</label>
                <Dropdown
                  value={productId}
                  onChange={(e) => {
                    setProductId(e.value);
                    setPageNumber(1);
                  }}
                  options={productOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Product"
                  showClear
                  disabled={loading}
                  filter
                  panelClassName="complaint-search-dropdown-panel"
                />
              </div>

              <div className="type-filter">
                <label className="filter-label">Manufacturing Unit</label>
                <Dropdown
                  value={manufacturingUnitId}
                  onChange={(e) => {
                    setManufacturingUnitId(e.value);
                    setPageNumber(1);
                  }}
                  options={manufacturingUnitOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Manufacturing Unit"
                  showClear
                  disabled={loading}
                  panelClassName="complaint-search-dropdown-panel"
                />
              </div>

              <div className="date-filter">
                <label className="filter-label">Manufactured From</label>
                <Calendar
                  value={manufactureDateFrom}
                  onChange={(e) => {
                    setManufactureDateFrom(e.value as Date);
                    setPageNumber(1);
                  }}
                  showIcon
                  icon="pi pi-calendar"
                  dateFormat="dd/mm/yy"
                  placeholder="Mfg From"
                  disabled={loading}
                  className="w-full compact-calendar"
                  panelClassName="complaint-search-datepicker"
                />
              </div>

              <div className="date-filter">
                <label className="filter-label">Manufactured To</label>
                <Calendar
                  value={manufactureDateTo}
                  onChange={(e) => {
                    setManufactureDateTo(e.value as Date);
                    setPageNumber(1);
                  }}
                  showIcon
                  icon="pi pi-calendar"
                  dateFormat="dd/mm/yy"
                  placeholder="Mfg To"
                  disabled={loading}
                  className="w-full compact-calendar"
                  panelClassName="complaint-search-datepicker"
                />
              </div>

              <div className="date-filter">
                <label className="filter-label">Expiry From</label>
                <Calendar
                  value={expiryDateFrom}
                  onChange={(e) => {
                    setExpiryDateFrom(e.value as Date);
                    setPageNumber(1);
                  }}
                  showIcon
                  icon="pi pi-calendar"
                  dateFormat="dd/mm/yy"
                  placeholder="Exp From"
                  disabled={loading}
                  className="w-full compact-calendar"
                  panelClassName="complaint-search-datepicker"
                />
              </div>

              <div className="date-filter">
                <label className="filter-label">Expiry To</label>
                <Calendar
                  value={expiryDateTo}
                  onChange={(e) => {
                    setExpiryDateTo(e.value as Date);
                    setPageNumber(1);
                  }}
                  showIcon
                  icon="pi pi-calendar"
                  dateFormat="dd/mm/yy"
                  placeholder="Exp To"
                  disabled={loading}
                  className="w-full compact-calendar"
                  panelClassName="complaint-search-datepicker"
                />
              </div>

              <div className="type-filter">
                <label className="filter-label">Batch Code</label>
                <InputText
                  value={batchCode}
                  onChange={(e) => {
                    setBatchCode(e.target.value);
                    setPageNumber(1);
                  }}
                  placeholder="e.g. B00000054"
                  disabled={loading}
                  className="batch-code-input"
                />
              </div>

              <div className="flex align-items-center gap-2" style={{ height: "34px" }}>
                <Checkbox
                  inputId="stockLessThanOne"
                  onChange={(e) => {
                    setOnlyAvailableStockLessThanOne(e.checked ?? false);
                    setPageNumber(1);
                  }}
                  checked={onlyAvailableStockLessThanOne}
                  disabled={loading}
                />
                <label htmlFor="stockLessThanOne" style={{ fontSize: "0.8125rem", cursor: "pointer", userSelect: "none", color: "#374151" }}>
                  Available Stock &lt; 1
                </label>
              </div>

              <div className="flex align-items-center gap-2" style={{ height: "34px" }}>
                <Checkbox
                  inputId="onlyHiddenBatches"
                  onChange={(e) => {
                    setOnlyHidden(e.checked ?? false);
                    setPageNumber(1);
                  }}
                  checked={onlyHidden}
                  disabled={loading}
                />
                <label htmlFor="onlyHiddenBatches" style={{ fontSize: "0.8125rem", cursor: "pointer", userSelect: "none", color: "#374151" }}>
                  Hidden Batches
                </label>
              </div>

              <div className="button-group flex gap-2">
                <Button
                  icon="pi pi-search"
                  onClick={handleFilterChange}
                  className="search-button-primary"
                  tooltip="Search"
                  tooltipOptions={{ position: 'top' }}
                  disabled={loading}
                />
                <Button
                  icon="pi pi-refresh"
                  onClick={handleResetFilters}
                  className="search-button-secondary"
                  tooltip="Reset"
                  tooltipOptions={{ position: 'top' }}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="mt-4">

            <ProductBatchDataTable
              data={batches}
              totalRecords={totalRecords}
              pageNumber={pageNumber}
              pageSize={pageSize}
              filters={{
                sourceTypeId,
                vendorId,
                subCategoryId,
                productId,
                manufacturingUnitId,
                manufactureDateFrom: manufactureDateFrom ? manufactureDateFrom.toISOString().split("T")[0] : null,
                manufactureDateTo: manufactureDateTo ? manufactureDateTo.toISOString().split("T")[0] : null,
                expiryDateFrom: expiryDateFrom ? expiryDateFrom.toISOString().split("T")[0] : null,
                expiryDateTo: expiryDateTo ? expiryDateTo.toISOString().split("T")[0] : null,
                onlyAvailableStockLessThanOne,
                onlyHidden,
              }}
              sortParams={{ sortBy, sortOrder }}
              setLoading={setLoading}
              onPageChange={(newPageNumber, newPageSize) => {
                setPageNumber(newPageNumber);
                setPageSize(newPageSize);
              }}
              onSortChange={(newSortBy, newSortOrder) => {
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
              }}
              onEdit={handleEditBatch}
              onDataRefresh={loadBatches}
            />
          </div>{/* End of mt-4 */}
        </div>{/* End of layout-content */}


        {/* Dialog */}
        <ProductBatchDialog
          visible={showDialog}
          editingBatch={editingBatch}
          metadata={metadata}
          onClose={handleDialogClose}
          onSuccess={handleDialogSuccess}
          setLoading={setLoading}
        />
        {loading && <AppLoading />}
      </Suspense>
    </>
  );
};

export default ProductBatchSummary;